<template>
  <ValidationObserver ref="form" #default="{ dirty, valid }">
    <main class="content">
      <div class="content__heading content__heading--navigation">
        <h1>
          <i class="icon--users"></i> {{ $t('users.listTitle') }}
          <small v-if="userList !== 'undefined'">({{ $store.getters['user/total'] }})</small>
        </h1>

        <nav class="navigation--buttonGroup">
          <button @click="showModal" class="navigation--buttonGroup__text">
            <i class="icon--plus"></i> {{ $t('users.add') }}
          </button>
        </nav>
      </div>

      <section class="content--boxed">

        <input :placeholder="$t('search.users')" type="text" name="search" v-model="search" :required="false"
               class="input input--search"/>

        <div class="table table--badged" v-if="userList.length > 0">

          <div class="table--badged__row" v-for="user in userList"
               :key="user.uuid" :class="user.deleted_at !== null ? 'disabled' : ''">

            <div class="table--badged__badge table--badged__badge--light"><i
              :class="user.deleted_at !== null ? 'icon--userx' : 'icon--user'"></i></div>

            <div class="table--badged__description">
              <h3>
                {{ user.name }}
                <small v-if="user.email_verified_at === null">({{ $t('users.pending') }})</small>
                <small v-if="user.deleted_at !== null">({{ $t('users.deactivated') }})</small>
              </h3>
              <small>
                <span class="table--badged__description__item" v-if="user.role_display !== 'undefined'"><i
                  class="icon--calendar"></i> {{ user.role_display }}</span>
                <span class="table--badged__description__item" v-if="user.email !== 'undefined'"><i
                  class="icon--calendar"></i> {{ user.email }}</span>
                <span class="table--badged__description__item" v-if="user.deleted_at !== null"><i
                  class="icon--calendar"></i> {{ user.deleted_at | moment('MMM D YYYY, HH:mm') }}</span>
              </small>
            </div>
            <div class="navigation--buttonGroup navigation--buttonGroup--table">

              <button v-if="$can('edit', 'users')"
                      @click.stop=prepEdit(user)><i class="icon--edit"></i>
              </button>
              <template v-if="user.email_verified_at">
                <button v-if="!user.deleted_at && $can('delete', 'users')"
                        @click.stop=disable(user.uuid)><i class="icon--toggleOn"></i>
                </button>
                <button v-if="user.deleted_at && $can('delete', 'users')"
                        @click.stop=enable(user.uuid)><i class="icon--toggleOff"></i>
                </button>
              </template>
              <button v-if="$can('delete', 'users')"
                      @click.stop="$modal.show('confirmation', { id: user.uuid })"><i class="icon--delete"></i>
              </button>
            </div>

          </div>

        </div>
        <template v-if="total === 0">
          <div class="message" v-if="!isFilterApplied">
            {{ $t('messages.noUsers') }}
          </div>
          <div class="message" v-else>
            {{ $t('messages.noResults') }}
          </div>
        </template>

      </section>

      <DqPaginate v-if="lastPage > 1" v-model="page" :page-count="lastPage"
                  :click-handler="dispatchList"/>
      <ModalUser :edit="edit" @clearEdit="clearEdit" @hideModal="hideModal" @init="init"/>
      <Confirm @confirmed="forceDelete"/>
    </main>

  </ValidationObserver>

</template>

<script>
import { ROLES } from '@/constants'
import _debounce from 'lodash.debounce'
import ModalUser from '@/components/Modal/User'
import { errHandler } from '@/helpers'
import DqPaginate from '@/components/Parts/DqPaginate'
import Confirm from '@/components/Modal/Confirm'
import { mapGetters } from 'vuex'

export default {
  name: 'User-Index',
  components: {
    DqPaginate,
    ModalUser,
    Confirm
  },
  beforeMount () {
    this.$store.commit('loader/startIntLoading')
  },
  async mounted () {
    await this.$store.dispatch('user/resetFilters')
    this.init(true)
    this.debouncedSearch = _debounce(async () => {
      await this.$store.dispatch('user/resetPage', { resetPage: true })
      this.dispatchList()
    }, 200)
    if (this.$route.query.open) {
      this.showModal()
    }
  },
  data: () => {
    return {
      form: {
        name: '',
        email: '',
        role: null
      },
      submit: false,
      edit: false,
      type_list: ROLES.COMPANY,
      columns: [
        {
          key: 'name',
          label: 'general.name',
          sortable: true
        },
        {
          key: 'role_display',
          label: 'general.role',
          sortable: true
        },
        {
          label: 'general.actions',
          permission: {
            subject: 'users',
            perms: ['edit', 'delete']
          },
          key: 'actions',
          sortable: true,
          htmlContent: true
        }
      ]
    }
  },
  computed: {
    ...mapGetters('user', ['userList', 'lastPage', 'total', 'isFilterApplied']),
    search: {
      get () {
        return this.$store.getters['user/search']
      },
      set (value) {
        this.$store.commit('user/setSearch', value)
      }
    },
    page: {
      get () {
        return this.$store.getters['user/currentPage']
      },
      set (value) {
        this.$store.commit('user/setPage', value)
      }
    }
  },
  methods: {
    async init (resetPage = false) {
      try {
        await this.$store.dispatch('location/resetPage', { resetPage })
        this.dispatchList().then((res) => {
          if (this.$route.query.edit) {
            let id = this.$route.query.edit
            let item = this.userList.find(function (elem) {
              if (elem.uuid === id) return elem
            })
            this.prepDashboardEdit(item)
          }
        })
      } catch (e) {
        errHandler(e, this.$toasted, key => this.$t(key))
      }
    },
    async dispatchList () {
      try {
        await this.$store.dispatch('user/get')
      } catch (e) {
        errHandler(e, this.$toasted, key => this.$t(key))
      } finally {
        this.$store.dispatch('loader/stopIntLoading')
      }
    },
    showModal () {
      this.$modal.show('add-user')
    },
    hideModal () {
      this.edit = false
      this.$modal.hide('add-user')
    },
    clearEdit () {
      this.edit = false
    },
    prepEdit (item) {
      this.edit = item
      this.showModal()
    },
    prepDashboardEdit (item) {
      if (this.$can('edit', 'users')) {
        this.edit = item
        this.showModal()
      }
      this.$router.replace({ name: 'user.index' })
    },
    async disable (id) {
      if (this.submit) { return false }
      try {
        this.submit = true
        await this.$store.dispatch('user/delete', id)
        this.init()
        this.$toasted.global.success_message({
          message: this.$t('users.deleteSuccess')
        })
      } catch (e) {
        errHandler(e, this.$toasted, key => this.$t(key))
      } finally {
        this.submit = false
      }
    },
    async enable (id) {
      if (this.submit) { return false }
      try {
        this.submit = true
        await this.$store.dispatch('user/enable', id)
        this.init()
        this.$toasted.global.success_message({
          message: this.$t('users.enableSuccess')
        })
      } catch (e) {
        errHandler(e, this.$toasted, key => this.$t(key))
      } finally {
        this.submit = false
      }
    },
    async forceDelete ({ id }) {
      if (this.submit) { return false }
      try {
        this.submit = true
        await this.$store.dispatch('user/forceDelete', id)
        this.init()
        this.$toasted.global.success_message({
          message: this.$t('users.forceDeleteSuccess')
        })
        this.$modal.hide('confirmation')
      } catch (e) {
        errHandler(e, this.$toasted, key => this.$t(key))
      } finally {
        this.submit = false
      }
    }
  },
  watch: {
    search () {
      this.debouncedSearch()
    }
  }
}
</script>
